import * as React from 'react';
import { connect } from 'react-redux';
import { ConversationSection } from './conversation-section';
import { IAppState } from '../../../app-state';
import { IConversationMessage } from './interfaces/state';
import { ICreateConversationPayload, ICreateConversationMessagePayload, ICreateNotePayload, IGetNotesPayload } from './interfaces/action';
import * as Actions from './actions';
import { EntityType, ConversationSectionEnum } from '../../../app-constants';
import './conversation.scss';
import { UserProfile } from '@chr/online-shared-microsite-core';

interface IConversationContainerStateProps {
  focusSectionId: ConversationSectionEnum;
  readOnly: boolean;
  conversationError: Error;
  conversationLoading: boolean;
  messages: IConversationMessage[];
  notes: IConversationMessage[];
  purchaseOrderId: number;
  conversationId: number;
  notesLoading: boolean;
  notesError: Error;
  customerCode: string;
  supplierCode: string;
  entityId: number;
  userProfile: UserProfile;
};

interface IConversationContainerDispatchProps {
  postConversation: (payload: ICreateConversationPayload) => void;
  postConversationMessage: (payload: ICreateConversationMessagePayload) => void;
  hideConversation: () => void;
  getConversation: (conversationId: number, purchaseOrderId: number) => void;
  postNote: (payload: ICreateNotePayload) => void;
  getNotes: (payload: IGetNotesPayload) => void;
}

type IConversationContainerProps = IConversationContainerStateProps & IConversationContainerDispatchProps;

const ConversationContainer = (props: IConversationContainerProps) => {

  const [conversationMessage, setConversationMessage] = React.useState('');
  const [notesMessage, setNotesMessage] = React.useState('');
  const [messageInitiallyFocused, setMessageInitiallyFocused] = React.useState(false);
  const [notesInitiallyFocused, setNotesInitiallyFocused] = React.useState(false);

  const articleRef = React.useCallback((article: HTMLElement) => {
    if (article !== null) {
      article.scrollTop = article.scrollHeight;
    }
  }, [props.messages]);

  const enterMessageRef = React.useCallback((node: HTMLInputElement) => {
    // Focus on message input when user has clicked on conversations or has entered a message
    if (node !== null
      && (
        (!messageInitiallyFocused && props.focusSectionId === ConversationSectionEnum.Conversation)
        || messageInitiallyFocused)
    ) {
      node.focus();
      setMessageInitiallyFocused(true);
    }
  }, [props.messages]);

  const notesArticleRef = React.useCallback((article: HTMLElement) => {
    if (article !== null) {
      article.scrollTop = article.scrollHeight;
    }
  }, [props.notes]);

  const notesMessageRef = React.useCallback((node: HTMLInputElement) => {
    // Focus on notes input when user has clicked on flag or has entered a note
    if (node !== null
      && (
        (!notesInitiallyFocused && props.focusSectionId === ConversationSectionEnum.Notes)
        || notesInitiallyFocused)
    ) {
      node.focus();
      setNotesInitiallyFocused(true);
    }
  }, [props.notes]);

  const handleConversationSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (props.conversationId) {
      props.postConversationMessage({
        conversationId: props.conversationId,
        message: conversationMessage,
        entityId: props.entityId,
        entityType: EntityType.PurchaseOrder
      });
    } else {
      props.postConversation({
        message: conversationMessage,
        entityId: props.purchaseOrderId,
        entityType: EntityType.PurchaseOrder
      });
    }
    setConversationMessage('');
  };

  const handleMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConversationMessage(event.target.value);
  };

  const handleNotesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNotesMessage(event.target.value);
  };

  const getConversation = () => {
    if (props.conversationId) {
      props.getConversation(props.conversationId, props.purchaseOrderId);
    }
  };

  const getNotes = () => {
    props.getNotes({
      customerCode: props.customerCode,
      supplierCode: props.supplierCode,
      entityId: props.purchaseOrderId,
      entityType: EntityType.PurchaseOrder
    });
  };

  const loggedInUserDisplayName = `${props.userProfile.firstName} ${props.userProfile.lastName}`;

  const handleNotesSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    props.postNote({
      createdByDisplayName: loggedInUserDisplayName,
      customerCode: props.customerCode,
      supplierCode: props.supplierCode,
      entityId: props.purchaseOrderId,
      entityType: EntityType.PurchaseOrder,
      note: notesMessage
    });
    setNotesMessage('');
  };

  return (
    <div className="conversationContainer">
      <header>
        <div className="flex">
          <div data-qa="level-title" className="bold">
            PO Conversation
          </div>
        </div>
        <button
          style={{ marginTop: 7 }}
          type="button"
          className="ns-icon ns-close"
          data-qa="close-conversation"
          onClick={props.hideConversation} />
      </header>
      <table width="100%">
        <tbody>
          <tr>
            <td style={{ verticalAlign: "top" }}>
              <ConversationSection
                sectionId={ConversationSectionEnum.Conversation}
                isExpanded={props.focusSectionId === ConversationSectionEnum.Conversation}
                isFirstSection={true}
                data-qa="conversation-section"
                error={props.conversationError}
                sectionName="Conversation"
                loading={props.conversationLoading}
                messages={props.messages}
                currentMessage={conversationMessage}
                loggedInUserDisplayName={loggedInUserDisplayName}
                readOnly={props.readOnly}
                onSubmit={handleConversationSubmit}
                onMessageChange={handleMessageChange}
                submitButtonName="Send"
                placeHolderText="Enter Message. . ."
                articleRef={articleRef}
                enterMessageRef={enterMessageRef}
                onRetryClick={getConversation}
              />
            </td>
          </tr>
          <tr>
            <td style={{ verticalAlign: "top" }}>
              <ConversationSection
                sectionId={ConversationSectionEnum.Notes}
                isExpanded={props.focusSectionId === ConversationSectionEnum.Notes}
                isFirstSection={false}
                data-qa="notes-section"
                error={props.notesError}
                sectionName="My Notes"
                loading={props.notesLoading}
                messages={props.notes}
                currentMessage={notesMessage}
                loggedInUserDisplayName={loggedInUserDisplayName}
                readOnly={props.readOnly}
                onSubmit={handleNotesSubmit}
                onMessageChange={handleNotesChange}
                submitButtonName="Save"
                placeHolderText="Enter Note. . ."
                articleRef={notesArticleRef}
                enterMessageRef={notesMessageRef}
                onRetryClick={getNotes}
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const mapStateToProps = (state: IAppState): IConversationContainerStateProps => {
  return {
    focusSectionId: state.conversationState.sectionId,
    readOnly: state.conversationState.readOnly,
    conversationError: state.conversationState.error,
    conversationLoading: state.conversationState.loading,
    messages: state.conversationState.messages,
    purchaseOrderId: state.poDetailsState.purchaseOrderId,
    conversationId: state.conversationState.conversationId,
    notes: state.notesState.notes,
    notesLoading: state.notesState.loading,
    notesError: state.notesState.error,
    customerCode: state.poDetailsState.customerCode,
    supplierCode: state.poDetailsState.supplierCode,
    entityId: state.conversationState.entityId,
    userProfile: state.userProfileState.userProfile,
  };
};

const mapDispatchToProps = (dispatch: any): IConversationContainerDispatchProps => {
  return {
    postConversation: (payload: ICreateConversationPayload) => {
      dispatch(Actions.createConversationPostAction(payload));
    },
    postConversationMessage: (payload: ICreateConversationMessagePayload) => {
      dispatch(Actions.conversationPostAction(payload));
    },
    hideConversation: () => {
      dispatch(Actions.conversationHideAction());
    },
    getConversation: (conversationId: number, purchaseOrderId: number) => {
      dispatch(Actions.conversationRequestAction({
        conversationId,
        entityId: purchaseOrderId,
        entityType: EntityType.PurchaseOrder
      }));
    },
    postNote: (payload: ICreateNotePayload) => {
      dispatch(Actions.notePostAction(payload));
    },
    getNotes: (payload: IGetNotesPayload) => {
      dispatch(Actions.notesRequestAction(payload));
    }
  };
};

export const ConnectedConversationContainer = connect<IConversationContainerStateProps, IConversationContainerDispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(ConversationContainer);
